import React, { useEffect, useRef, useState } from 'react';
import { Grid, Theme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import OnboardingFooter from './OnboardingFooter';
import * as yup from 'yup';
import { Formik, FormikProps } from 'formik';
import { useAppSelector } from '../../../hooks';
import { SellingLocationOption } from '@castiron/domain';
import AdminForm from '../../AdminForm';
import {
  LocalMarketsIcon,
  OnlineStoreIcon,
  PopUpEventsIcon,
  RetailStoreIcon,
  RadioOrCheckboxGroup,
  Banner,
  Typography,
} from '@castiron/components';
import { accountRepository } from '../../../domain';
import { useTracking } from '@castiron/utils';

interface Props {
  step: number;
  setHeader?: (header: string) => void;
  setSubHeader?: (subHeader: string) => void;
  nextStep?: () => void;
  prevStep?: () => void;
}

const useStyles = makeStyles((theme: Theme) => ({}));

const sellingLocationsSchema = yup.object().shape({
  sellingLocations: yup
    .array()
    .min(1, 'Please select at least one option.')
    .of(yup.string().oneOf(['online', 'local-markets', 'retail-store', 'other-pop-up'])),
});

const SellingLocation: React.FC<Props> = (props: Props) => {
  const { step, nextStep, prevStep, setHeader, setSubHeader } = props;
  const classes = useStyles();
  const formRef = useRef<FormikProps<any>>();
  const { trackEvent } = useTracking();
  const [showError, setShowError] = useState(false);

  const checkboxList = [
    {
      icon: <OnlineStoreIcon />,
      label: 'Online Store',
      value: 'online',
    },
    {
      icon: <LocalMarketsIcon />,
      label: 'Local Markets',
      value: 'local-markets',
    },
    {
      icon: <RetailStoreIcon />,
      label: 'Retail Storefront',
      value: 'retail-store',
    },
    {
      icon: <PopUpEventsIcon />,
      label: 'Other Events/Pop Ups',
      value: 'other-pop-up',
    },
  ];

  const { account } = useAppSelector(state => ({
    account: state.shops.account,
  }));

  useEffect(() => {
    if (
      account?.onboardingQuestions?.sellingLocations &&
      account?.onboardingQuestions?.sellingLocations.length > 0 &&
      formRef?.current?.values.sellingLocations.length === 0
    ) {
      formRef?.current?.setFieldValue('sellingLocations', account.onboardingQuestions.sellingLocations);
    }
  }, [account]);

  useEffect(() => {
    setHeader('Where do you sell your products?');
    setSubHeader(
      'This helps us suggest the right package, suggest product types, and provide relevant resources. Please select all that apply.',
    );
  }, []);

  const submit = async () => {
    const { sellingLocations } = formRef.current.values;
    trackEvent('Onboarding Selling Locations', { sellingLocations: sellingLocations });
    await accountRepository.updateProps(account.id, {
      'onboardingQuestions.sellingLocations': sellingLocations,
    });

    nextStep();
  };

  const onNextClick = async () => {
    await formRef.current.validateForm();
    if (formRef.current.isValid) {
      formRef.current.submitForm();
    } else {
      setShowError(true);
    }
  };

  const handleCheckboxChange = (value: SellingLocationOption, formikProps: FormikProps<any>) => {
    setShowError(false);
    const { sellingLocations } = formikProps.values;
    let newLocations = [];
    if (sellingLocations.includes(value)) {
      newLocations = sellingLocations.filter(category => category !== value);
    } else {
      newLocations = [...sellingLocations, value];
    }
    formikProps.setFieldValue('sellingLocations', newLocations);
  };

  return (
    <Grid container direction="column">
      {showError && (
        <Grid item style={{ marginTop: '24px' }}>
          <Banner variant="error">
            <Typography variant="body2" style={{ color: 'inherit' }}>
              Please select at least one option.
            </Typography>
          </Banner>
        </Grid>
      )}
      <Grid item>
        <Formik
          initialValues={{
            sellingLocations: [],
          }}
          validationSchema={sellingLocationsSchema}
          onSubmit={submit}
          innerRef={formRef}
        >
          {(formikProps: FormikProps<any>) => (
            <AdminForm>
              <Grid item xs={12}>
                <RadioOrCheckboxGroup
                  inputType="checkbox"
                  items={checkboxList}
                  selectedItem={formikProps.values.sellingLocations}
                  setSelection={(value: SellingLocationOption) => handleCheckboxChange(value, formikProps)}
                />
              </Grid>
            </AdminForm>
          )}
        </Formik>
      </Grid>
      <OnboardingFooter step={step} nextStep={onNextClick} prevStep={prevStep} />
    </Grid>
  );
};

export default SellingLocation;
